// NewLumen Link — the free referring-practice tier.
//
// Two endpoint families, split by owning service (see
// docs/planning/2026-07-29-newlumen-link-spec.md §5):
//
//   ControlPlane (anonymous enroll + redemption + email verification):
//     /auth/link/* and /auth/verify-email. The '/auth' prefix is deliberate —
//     client.ts routes '/auth' paths to the ControlPlane base AND exempts them
//     from the session-scoped request guard, which is exactly what the
//     pre-session preview/redeem/verify calls need.
//     ('/auth/link/redeem-existing' and '/auth/link/resend-verification' are
//     the two authenticated exceptions; both are registered in
//     isAuthenticatedAuthRoute in client.ts.)
//
//   Regional WebApi (authenticated Link surfaces): /link/* and
//     /teleconsultation/groups/{id}/link-* — not ControlPlane prefixes, so they
//     route to the active region like every other clinic-scoped call.
//
import { ApiError, apiFetch, apiRequest, apiUpload, getAccessToken, resolveApiBase } from './client';
// ---------- ControlPlane functions ----------
export async function previewLinkEnrollment(code) {
    return apiFetch(`/auth/link/preview/${encodeURIComponent(code)}`);
}
export async function redeemLinkCode(request) {
    return apiRequest('POST', '/auth/link/redeem', request);
}
/** Adds the code's group as a destination on the caller's existing Link account. */
export async function redeemLinkExisting(code) {
    return apiRequest('POST', '/auth/link/redeem-existing', { code });
}
// ---------- ControlPlane: email verification ----------
/**
 * POST /auth/verify-email — anonymous. The ControlPlane collapses every
 * failure mode (unknown, expired, malformed token) into the same generic 410
 * so the verification landing page can't be used to probe token validity, and
 * answers 200 empty both the first time a token is used AND on a stale
 * re-click of an already-consumed link — the caller cannot tell the two
 * apart and must not try to.
 */
export async function verifyEmail(token) {
    return apiRequest('POST', '/auth/verify-email', { token });
}
/**
 * POST /auth/link/resend-verification — authenticated (see
 * isAuthenticatedAuthRoute in client.ts), no body. Always answers 200: the
 * endpoint never discloses through its response whether the address was
 * already verified.
 */
export async function resendVerification() {
    return apiRequest('POST', '/auth/link/resend-verification');
}
// ---------- Regional functions: Link practice ----------
export async function getLinkSetup() {
    return apiFetch('/link/setup');
}
export async function updateLinkAllowlist(allowedSourceCidrs) {
    return apiRequest('PUT', '/link/setup/allowlist', { allowedSourceCidrs });
}
export async function getLinkCaseReport(caseId) {
    return apiFetch(`/link/cases/${encodeURIComponent(caseId)}/report`);
}
/** Finalized report as PDF — same letterhead rendering the group's own PDFs use. */
export async function getLinkReportPdf(caseId) {
    const endpoint = `/link/cases/${encodeURIComponent(caseId)}/report/pdf`;
    const token = getAccessToken();
    const headers = {};
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${resolveApiBase(endpoint)}${endpoint}`, {
        headers,
        credentials: 'include',
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'Report PDF generation failed'));
    }
    return response.blob();
}
export async function getLinkIngestActivity() {
    return apiFetch('/link/ingest-activity');
}
// ---------- Regional functions: group-admin clinic links ----------
export async function getLinkTokens(groupId) {
    return apiFetch(`/teleconsultation/groups/${encodeURIComponent(groupId)}/link-tokens`);
}
export async function createLinkToken(groupId, request) {
    return apiRequest('POST', `/teleconsultation/groups/${encodeURIComponent(groupId)}/link-tokens`, request);
}
/** Branded one-page PDF handout carrying the enrollment URL + QR (spec §5.1). */
export async function getLinkTokenHandout(groupId, tokenId) {
    const endpoint = `/teleconsultation/groups/${encodeURIComponent(groupId)}/link-tokens/${encodeURIComponent(tokenId)}/handout`;
    // Authenticated raw fetch for a binary body — mirrors getTcReportPdfBlob.
    const token = getAccessToken();
    const headers = {};
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${resolveApiBase(endpoint)}${endpoint}`, {
        headers,
        credentials: 'include',
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'Handout generation failed'));
    }
    return response.blob();
}
function linkTokenPath(groupId, tokenId) {
    return `/teleconsultation/groups/${encodeURIComponent(groupId)}/link-tokens/${encodeURIComponent(tokenId)}`;
}
/**
 * Stops the link enrolling anyone new. Idempotent (204 on an already-revoked
 * token); clinics that enrolled through it keep working.
 */
export async function revokeLinkToken(groupId, tokenId) {
    return apiRequest('POST', `${linkTokenPath(groupId, tokenId)}/revoke`);
}
/**
 * Soft-deletes a REVOKED link so it leaves the list. The redemption ledger
 * behind it survives (enrolled clinics stay enrolled). An active token answers
 * 409 — revoke first. Idempotent on an already-deleted token.
 */
export async function deleteLinkToken(groupId, tokenId) {
    return apiRequest('DELETE', linkTokenPath(groupId, tokenId));
}
/**
 * Renames the clinic a link is for. Works on active and revoked tokens; the
 * server trims the label, stores empty as NULL, and rejects > 200 chars with a
 * 400. Resolves the full list-row shape so the caller can patch its cache.
 */
export async function renameLinkToken(groupId, tokenId, label) {
    return apiRequest('PATCH', linkTokenPath(groupId, tokenId), { label });
}
export async function getLinkPractices(groupId) {
    return apiFetch(`/teleconsultation/groups/${encodeURIComponent(groupId)}/link-practices`);
}
export async function getLinkUsage(groupId) {
    return apiFetch(`/teleconsultation/groups/${encodeURIComponent(groupId)}/link-usage`);
}
export async function getGroupLogo(groupId) {
    return apiFetch(`/teleconsultation/groups/${encodeURIComponent(groupId)}/logo`);
}
/**
 * Replaces the group's logo. PNG/JPEG/WebP, 512 KB — the server rejects anything
 * else with a 400. Returns the fresh signed URL so the caller can swap its
 * preview without a second round trip.
 */
export async function uploadGroupLogo(groupId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload('PUT', `/teleconsultation/groups/${encodeURIComponent(groupId)}/logo`, formData);
}
/** Idempotent — removing an already-absent logo is a 204, not a 404. */
export async function removeGroupLogo(groupId) {
    return apiRequest('DELETE', `/teleconsultation/groups/${encodeURIComponent(groupId)}/logo`);
}
// Matches both `https://host/…` and the protocol-relative `//host/…`; anything
// else is a path this app has to resolve against a base itself.
const ABSOLUTE_URL = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;
/**
 * Resolves the enrollment preview's `groupLogoUrl` to something an `<img src>`
 * can load.
 *
 * The field arrives in one of two shapes and the landing page must not care
 * which: an absolute signed URL, or a ControlPlane-relative path under `/auth/`
 * (the same service that served the preview). Routing the relative form through
 * resolveApiBase is what makes it work everywhere — dev points at a separate
 * ControlPlane container, prod ships the literal `/api` and lets the load
 * balancer split control-plane paths off.
 */
export function resolveGroupLogoUrl(url) {
    if (!url)
        return null;
    if (ABSOLUTE_URL.test(url))
        return url;
    return `${resolveApiBase(url)}${url}`;
}
